import { AspectRatio } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';

import { TextField } from 'Containers/App/types';
import { normalizePlanName } from 'Containers/App/utils';
import { useTextContext } from 'Contexts/textContext';
import { H3, Paragraph } from 'DesignLibrary/atoms';
import { Drawer } from 'DesignLibrary/atoms/Drawer';
import { IconTypes } from 'DesignLibrary/atoms/Icon';
import Media from 'Shared/Media';
import Text from 'Shared/Text';
import { Recommendation, EducationalDrawerInfoTypes, SAVINGS_ACCOUNT_TYPES } from 'Types/entities';
import { FsaBanner } from 'Utils/helpers';
import { sendInsightsEvent } from 'Utils/insights';

import { EligBars } from './EligBars';
import { FAQ } from './FAQ';
import { HowItWorks } from './HowItWorks';
import { SupplementalContent, Header } from './styled';
import { WhatCanItDo } from './WhatCanItDo';

export interface EducationalDrawerProps {
  isOpen: boolean;
  handleClose: () => void;
  infoType: EducationalDrawerInfoTypes;
  // hsa specific
  individualHsaContributionLimit?: number;
  familyHsaContributionLimit?: number;
  // generic
  employerContribution: number | null;
  selectedPlan: Recommendation | Record<string, never>;
  // hra specific
  showLimitedProviderHraEducation?: boolean;
  showHsaHraComboEducation?: boolean;
  hdhpMinDeductible?: number | null;
  shouldShowEducationalVideo?: boolean;
  // Required when infoType is FSA - if omitted, no eligibility banner renders at all.
  fsaBanner?: FsaBanner;
}

const EducationalDrawer = ({
  isOpen,
  infoType,
  individualHsaContributionLimit,
  familyHsaContributionLimit,
  employerContribution,
  selectedPlan,
  showLimitedProviderHraEducation = false,
  showHsaHraComboEducation = false,
  hdhpMinDeductible,
  handleClose,
  shouldShowEducationalVideo,
  fsaBanner,
}: EducationalDrawerProps) => {
  const { retrieveContentfulData } = useTextContext();

  useEffect(() => {
    if (isOpen) {
      sendInsightsEvent(null, 'info_modal_view_started', {
        modal_id: infoType,
      });
    }
  }, [isOpen]);

  const handleOnClose = () => {
    sendInsightsEvent(null, 'info_modal_view_ended', { modal_id: infoType });
    handleClose();
  };

  const contentfulTitle: TextField = `health_section.secondary_content.${infoType}_modal.title`;
  const contentfulIcon: TextField = `health_section.secondary_content.${infoType}_modal.icon`;

  const video_url = `health_section.secondary_content.${infoType}_modal.video_url`;
  const contentfulHeader: TextField = `health_section.secondary_content.${infoType}_modal.header`;

  const isSelectedPlanEligibleForBenefit = () => {
    if (isEmpty(selectedPlan)) {
      return false;
    }

    if (infoType === SAVINGS_ACCOUNT_TYPES.HSA) {
      return selectedPlan?.plan.hsa_eligible;
    }
    if (infoType === SAVINGS_ACCOUNT_TYPES.HRA) {
      return selectedPlan?.plan.hra_eligible;
    }

    return false;
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleOnClose}
      title={
        infoType === 'no_deductible'
          ? `${contentfulHeader}.text`
          : retrieveContentfulData<string>(contentfulTitle)
      }
      titleIcon={retrieveContentfulData<IconTypes>(contentfulIcon)}
      vars={
        infoType === 'no_deductible'
          ? { x: normalizePlanName(selectedPlan?.plan?.plan_marketing_name) }
          : undefined
      }
      infoType={infoType}
    >
      <div data-testid={`educational-modal-${infoType}`}>
        <SupplementalContent>
          {infoType !== 'no_deductible' && (
            <EligBars
              infoType={infoType}
              isSelectedEligible={isSelectedPlanEligibleForBenefit()}
              employerContribution={employerContribution}
              fsaBanner={fsaBanner}
            />
          )}

          <article>
            <Header>
              {infoType !== 'no_deductible' && (
                <H3 as="h1">
                  <Text field={`${contentfulHeader}.text`} />
                </H3>
              )}
              <Paragraph>
                <Text
                  field={`${contentfulHeader}.subtext`}
                  vars={
                    infoType === 'no_deductible'
                      ? { x: normalizePlanName(selectedPlan?.plan?.plan_marketing_name) }
                      : undefined
                  }
                />
              </Paragraph>
              {infoType === 'no_deductible' && (
                <>
                  <br />
                  <Paragraph>
                    <Text
                      field={`${contentfulHeader}.${selectedPlan?.plan?.carrier_name === 'Surest' ? 'para_2_surest' : 'para_2'}`}
                      vars={{ x: normalizePlanName(selectedPlan?.plan?.plan_marketing_name) }}
                    />
                  </Paragraph>
                  <br />
                  <Paragraph>
                    <Text field={`${contentfulHeader}.para_3`} />
                    {selectedPlan?.plan?.carrier_name !== 'Surest' && (
                      <>
                        &nbsp;
                        <Text field={`${contentfulHeader}.para_3_additional_text`} />
                      </>
                    )}
                  </Paragraph>
                </>
              )}
              {shouldShowEducationalVideo && video_url && (
                <AspectRatio className="video" maxW="600px" ratio={16 / 9}>
                  <Media field={video_url} title={`${infoType} video`} form="video" />
                </AspectRatio>
              )}
            </Header>
            {infoType !== 'no_deductible' && (
              <>
                <section>
                  <HowItWorks
                    infoType={infoType}
                    employerContribution={employerContribution}
                    individualHsaContributionLimit={individualHsaContributionLimit}
                    familyHsaContributionLimit={familyHsaContributionLimit}
                    showLimitedProviderHraEducation={showLimitedProviderHraEducation}
                  />
                </section>
                <section>
                  <WhatCanItDo infoType={infoType} showHsaHraComboEducation={showHsaHraComboEducation} />
                </section>
                <section>
                  <FAQ
                    infoType={infoType}
                    showLimitedProviderHraEducation={showLimitedProviderHraEducation}
                    hdhpMinDeductible={hdhpMinDeductible}
                    employerHraContribution={
                      infoType === SAVINGS_ACCOUNT_TYPES.HRA ? employerContribution : null
                    }
                  />
                </section>
              </>
            )}
          </article>
        </SupplementalContent>
      </div>
    </Drawer>
  );
};

export default EducationalDrawer;
